<template>
  <div class="sorting">
    <div
      class="sorting__title"
      @click="togglePopoverHandler"
    >
      <SortingIcon />
      <span>{{ title }}</span>
    </div>
    <ui-popover
      v-if="!isMobile"
      :open="isOpenPopover"
      @close="closePopover"
    >
      <template #content>
        <div class="sorting__list">
          <div
            v-for="item of listSort"
            :key="item.value"
            class="sorting-desktop__list-item"
            @click="chooseSortingValue(item.value)"
          >
            <span>
              {{ item.label }}
            </span>
            <CheckMarkIcon v-if="isChosenSortValue(item.value)" />
          </div>
        </div>
      </template>
    </ui-popover>
  </div>
</template>

<script setup lang="ts">
  import { useModal, type IModalController } from '@experience/core-modal-ui';
  import { useScreenSize } from '@experience/shared';
  import { UiPopover } from '@experience/vue3-kit';

  import { SortingIcon } from '@/shared/ui/icons/sortingIcon';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { ListSortItemValue } from '@/features/filter/sorting/const/config';
  import { CheckMarkIcon } from '@/shared/ui/icons/checkMarkIcon';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import { sortingScroll } from '@/shared/lib/use-scroll';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { computed, onUnmounted, ref, type useShadowRoot } from 'vue';
  import { useExcursionSortingClickTracker } from '@/features/filter/sorting/composables/excursion/useExcursionSortingClickTracker.ts';
  import { useHeaderZIndex } from '@/shared/composables/useHeaderZIndex.ts';

  interface Props {
    defaultSoguFields: ContextGeoSubject;
  }

  const props = defineProps<Props>();

  const { t } = useI18n({
    inheritLocale: true,
    messages: { ru },
  });

  const { isMobile } = useScreenSize();
  const filtersStore = useExcursionFiltersStore();
  const { trackSortingClick } = useExcursionSortingClickTracker();
  const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();
  const modal = useModal();

  const isOpenPopover = ref(false);
  const sortingModalController = ref<IModalController | null>(null);

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  const title = computed(() => {
    switch (filtersStore.searchState.sorting) {
      case ListSortItemValue.PUBLISHED_AT:
        return t('sorting.publishedAt');
      case ListSortItemValue.PRICE:
        return t('sorting.price');
      case ListSortItemValue.RATING:
        return t('sorting.rating');
      case ListSortItemValue.DISCOUNT:
        return t('sorting.discount');
      default:
        return t('sorting.rank');
    }
  });

  const listSort = computed(() => {
    const config = {
      rank: {
        label: t('sorting.rank'),
        value: ListSortItemValue.RANK,
      },
      price: {
        label: t('sorting.price'),
        value: ListSortItemValue.PRICE,
      },
      publishedAt: {
        label: t('sorting.publishedAt'),
        value: ListSortItemValue.PUBLISHED_AT,
      },
      rating: {
        label: t('sorting.rating'),
        value: ListSortItemValue.RATING,
      },
      discount: {
        label: t('sorting.discount'),
        value: ListSortItemValue.DISCOUNT,
      },
    } as const;

    type Key = keyof typeof config;

    const order = ['rank', 'publishedAt', 'price', 'rating'];

    return order.map((key, idx) => ({
      id: idx + 1,
      ...config[key as Key],
    }));
  });

  /**
   * Открывает мобильную сортировку через ленивый сервис модалок.
   */
  const openMobileSortingModal = () => {
    if (sortingModalController.value) {
      return;
    }

    decreaseZIndex();
    sortingModalController.value = modal.open({
      component: () => import('../FilterSortingModal.vue'),
      props: {
        title: t('sorting.title'),
        listSort: listSort.value,
        selectedValue: filtersStore.state.sorting,
        onClose: closeMobileSortingModal,
        onSelect: chooseSortingValue,
      },
    });
  };

  /**
   * Переключает видимость сортировки с учётом desktop и mobile вариантов.
   */
  const togglePopoverHandler = () => {
    if (isMobile.value) {
      openMobileSortingModal();

      return;
    }

    isOpenPopover.value = !isOpenPopover.value;
  };

  /**
   * Закрывает мобильную модалку сортировки и возвращает z-index header.
   */
  const closeMobileSortingModal = () => {
    sortingModalController.value?.close();
    sortingModalController.value = null;
    increaseZIndex();
  };

  /**
   * Закрывает desktop popover сортировки.
   */
  const closePopover = () => {
    isOpenPopover.value = false;
  };

  /**
   * Применяет выбранную сортировку и запускает существующий поиск.
   */
  const chooseSortingValue = (item: string) => {
    filtersStore.state.sorting = item;
    filtersStore.search();

    if (isMobile.value) {
      closeMobileSortingModal();
    } else {
      closePopover();
    }

    trackSortingClick(props.defaultSoguFields);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sortingScroll(rootNode, false);
      });
    });
  };

  const isChosenSortValue = (item: string) => {
    return item === filtersStore.state.sorting;
  };

  onUnmounted(() => {
    if (sortingModalController.value) {
      closeMobileSortingModal();
    }
  });
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .sorting {
    position: relative;
    z-index: 9;

    :deep(.popover) {
      right: 0;
      left: auto;
      overflow: hidden;
      padding: 0;
      border-radius: 8px;
    }
  }

  .sorting__title {
    display: flex;
    align-items: center;
    cursor: pointer;
    column-gap: 4px;

    @include typography.base-400-400-medium;

    @include adaptivity.tablet {
      @include typography.base-300-300-medium;
    }
  }

  .sorting__list {
    width: 200px;
    padding: 4px 0;
  }

  .sorting-desktop__list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    white-space: nowrap;
    cursor: pointer;

    @include typography.base-400-400-regular;

    &:hover {
      background: var(--comp-dropdown-menu-item-color-background-hovered);
    }
  }
</style>
